import Immutable from 'immutable';
import moment from 'moment';
import _values from 'lodash/values';

import HoldApi from '../api/HoldApi';
import { MATERIAL_TYPE_PHYSICAL } from '../constants/BibConstants';
import AuthConstants from '../constants/AuthConstants';
import HoldsConstants, { WORKFLOW_VIEWS, HOLD_DATE_FORMAT } from '../constants/HoldsConstants';
import ShelfActions from './ShelfActions';
import { ITEM_TRANSACTION_WORKFLOW } from '../constants/WorkflowConstants';
import { currentAccountSelector } from '../selectors/AuthSelector';
import { sortedPickupBranchSelector, currentLibrarySelector } from '../selectors/LibrarySelector';
import WorkflowActions from './WorkflowActions';
import composeSuccessMessaging from '../helpers/messaging/composeSuccessMessaging';
import composeGlobalSuccessMessaging from '../helpers/messaging/composeGlobalSuccessMessaging';
import composeGlobalFailureMessaging from '../helpers/messaging/composeGlobalFailureMessaging';
import composeFailureMessaging from '../helpers/messaging/composeFailureMessaging';
import { getBibsByMetadataId } from '../selectors/BibSelector';
import composeMessagingForBatchAction from '../helpers/borrowing/composeMessagingForBatchAction';
import composeMessaging from '../helpers/messaging/composeMessaging';
import { STATUS_TYPES, GLOBAL_MESSAGING_ID } from '../constants/MessagingConstants';

function shouldUseSingleClickHolds(singleClickHoldsSettings, materialType, itemLevelHold, isValidLocation) {
  return singleClickHoldsSettings && singleClickHoldsSettings.get('enabled')
  && materialType === MATERIAL_TYPE_PHYSICAL && !itemLevelHold && isValidLocation;
}

function isValidSingleClickHoldsLocation(branches, branchCode) {
  const singleClickHoldsCheck = branches && branches.filter(branch => branch.get('code') === branchCode);
  return singleClickHoldsCheck && singleClickHoldsCheck.size > 0;
}

function placeHoldApiSuccess(messagingId, response, store) {
  const { successCount } = response;
  let values = { count: successCount, position: 0 };
  const holds = _values(response.entities.holds) || [];
  const firstHold = holds[0];
  if (firstHold) {
    const bibs = getBibsByMetadataId(store.getState());
    const title = bibs.getIn([firstHold.metadataId, 'briefInfo', 'title'], '');
    const position = firstHold.holdsPosition || 0;
    values = { ...values, position, title };
  }

  let messageId = 'message_item_holds_placed_no_position';
  if (successCount === 1) {
    messageId = (values.position > 0) ? 'message_item_hold_placed' : 'message_item_hold_placed_no_position';
  }

  return {
    messaging: messagingId ?
      composeSuccessMessaging(messagingId, messageId, { values }) :
      composeGlobalSuccessMessaging(messageId, { values })
  };
}

export function placeHold({ workflowId, messagingId, accountId, metadataId, materialType, enableSingleClickHolds = false, materialParams }) {
  return {
    workflowId,
    messagingId,
    payload: {
      id: metadataId,
      selectedBranch: materialParams.branchId
    },
    middlewareData: {
      accountRequired: true
    },
    types: [
      HoldsConstants.PLACE_HOLD_REQUEST,
      HoldsConstants.PLACE_HOLD_SUCCESS,
      HoldsConstants.PLACE_HOLD_FAILURE
    ],
    apiRequest: (state, apiClient) => HoldApi.placeHold({
      metadataId,
      materialType,
      accountId,
      enableSingleClickHolds,
      materialParams
    }, apiClient),
    apiSuccess: (response, store) => placeHoldApiSuccess(messagingId, response, store),
    apiFailure: ({ error }) => {
      // Special case for when the library doesn't own the digital bib.
      // TODO: This was an old logic migrated from the api-gateway. Investigate whether we still need it or if it should be solved differently.
      if (error && error.classification === 'HoldNotAllowed') {
        return {
          entities: {
            bibs: {
              [metadataId]: {
                availability: {
                  status: 'RESOURCE_NOT_FOUND',
                  localisedStatus: 'RESOURCE_NOT_FOUND'
                }
              }
            }
          }
        };
      }

      return {};
    }
  };
}

/**
 * Place an Item Level Hold on one or more items of a Bib
 *
 * @param {String} metadataId metadataId of the bib
 * @param {Array<String>} itemIds itemIds to place hold on
 * @param {String} branchId branch to place the hold at
 */
export function placeItemHold({ metadataId, itemIds, branchId, messagingId, workflowId }) {
  return {
    messagingId,
    workflowId,
    payload: {
      id: metadataId
    },
    middlewareData: {
      accountRequired: true
    },
    types: [
      HoldsConstants.PLACE_HOLD_REQUEST,
      HoldsConstants.PLACE_HOLD_SUCCESS,
      HoldsConstants.PLACE_HOLD_FAILURE
    ],
    apiRequest: (state, apiClient) =>
      HoldApi.placeHold({
        metadataId,
        accountId: currentAccountSelector(state).get('id'),
        materialType: MATERIAL_TYPE_PHYSICAL,
        materialParams: {
          branchId,
          errorMessageLocale: state.getIn(['localization', 'currentLanguage']),
          itemIds
        }
      }, apiClient),
    apiSuccess: (response, store) => placeHoldApiSuccess(messagingId, response, store)
  };
}

export function cancelHold({ accountId, metadataIds, holdIds, shelfState, messagingId, workflowId, isBatch = false }) {
  return {
    workflowId,
    messagingId,
    isBatch,
    holdIds,
    middlewareData: {
      accountRequired: true
    },
    types: [
      HoldsConstants.CANCEL_HOLD_REQUEST,
      HoldsConstants.CANCEL_HOLD_SUCCESS,
      HoldsConstants.CANCEL_HOLD_FAILURE
    ],
    apiRequest: (state, apiClient) => (HoldApi.cancelHold({ accountId, holdIds, metadataIds }, apiClient)),
    apiSuccess: ({ failures }) => ({
      deletedEntities: holdIds.map(id => `holds.${id}`),
      messaging: messagingId ?
        composeSuccessMessaging(messagingId, 'message_successful_cancel_holds') :
        composeMessagingForBatchAction({
          total: holdIds.length,
          failures,
          successId: 'message_successful_cancel_holds',
          mixedId: 'message_mixed_cancel_holds',
          failureId: 'message_failure_cancel_holds_one_or_more'
        })
    }),

    apiFailure: (response) => {
      let messaging;

      if (holdIds.length > 1) {
        messaging = composeMessaging(
          STATUS_TYPES.Failure,
          messagingId || GLOBAL_MESSAGING_ID,
          'message_failure_cancel_holds_one_or_more'
        );
      } else {
        messaging = messagingId ?
          composeFailureMessaging(messagingId, response.error) :
          composeGlobalFailureMessaging(response.error);
      }

      return { messaging };
    },

    postSuccess: () => {
      if (shelfState && shelfState.toAdd) {
        return (isBatch || metadataIds.length > 1) ?
          ShelfActions.batchAddToShelf({
            metadataIds,
            shelfName: shelfState.name
          }) :
          ShelfActions.addToShelf(metadataIds[0], shelfState.name);
      }
      return () => { };
    }
  };
}


export function startPlaceHold(workflowId, metadataId, messagingId) {
  const determineHoldFlow = (dispatch, getState) => {
    const state = getState();
    const auth = state.get('auth');

    if (!auth.get('currentUserId')) {
      return dispatch({ type: AuthConstants.OPEN_LOGIN_OVERLAY, payload: { postLoginAction: determineHoldFlow } });
    }

    const account = currentAccountSelector(state);
    const library = currentLibrarySelector(state);
    const policy = state.getIn(['entities', 'bibs', metadataId, 'policy']);
    const materialType = policy.get('materialType');
    const itemLevelHold = policy.get('itemLevelHoldableItems') && policy.getIn(['materialPolicy', 'itemLevelHoldsPredetermined']);
    const singleClickHoldsSettings = account ? account.get('singleClickHoldsSettings', Immutable.Map()) : Immutable.Map();
    const accountId = account && account.get('id');
    const currentLocale = state.getIn(['localization', 'currentLanguage'], 'en-US');
    const requiresExpiryDate = library.get('requiresHoldExpiryDate', false);
    const branches = sortedPickupBranchSelector(state);

    if (shouldUseSingleClickHolds(singleClickHoldsSettings, materialType, itemLevelHold, isValidSingleClickHoldsLocation(branches, singleClickHoldsSettings.get('branchId')))) {
      return dispatch(
        placeHold({
          workflowId,
          accountId,
          metadataId,
          messagingId,
          materialType,
          enableSingleClickHolds: false,
          materialParams: {
            branchId: singleClickHoldsSettings.get('branchId'),
            expiryDate: requiresExpiryDate ? moment().add(singleClickHoldsSettings.get('holdsDurationExpiration'), 's').format(HOLD_DATE_FORMAT) : null,
            errorMessageLocale: currentLocale
          }
        })
      );
    }

    const physicalHoldWorkflowView =
      itemLevelHold ? WORKFLOW_VIEWS.ITEM_LEVEL_HOLD : WORKFLOW_VIEWS.PHYSICAL_HOLD;

    return dispatch(
      WorkflowActions.updateWorkflow(
        workflowId,
        ITEM_TRANSACTION_WORKFLOW,
        Immutable.Map({
          metadataId,
          messagingId,
          step: materialType === MATERIAL_TYPE_PHYSICAL ?
            physicalHoldWorkflowView :
            WORKFLOW_VIEWS.DIGITAL_HOLD
        }),
        true,
        true
      )
    );
  };

  return determineHoldFlow;
}

export function startCancelHold(workflowId, metadataId, messagingId) {
  return WorkflowActions.updateWorkflow(
    workflowId,
    ITEM_TRANSACTION_WORKFLOW,
    Immutable.Map({
      metadataId,
      step: WORKFLOW_VIEWS.CANCEL_HOLD,
      messagingId
    }),
    true,
    true
  );
}

/**
 * Fetches the Item Groupings for the Hold
 * @param {String} metadataId
 */
export function getItemGroups(metadataId) {
  return {
    payload: {
      id: metadataId
    },
    types: [
      HoldsConstants.FETCH_ITEM_GROUPS_REQUEST,
      HoldsConstants.FETCH_ITEM_GROUPS_SUCCESS,
      HoldsConstants.FETCH_ITEM_GROUPS_FAILURE
    ],
    apiRequest: (state, apiClient) =>
      HoldApi.fetchItemGroups(metadataId, apiClient)
  };
}

export function updatePickupLocation(metadataId, pickupLocation) {
  return {
    payload: {
      id: metadataId,
      pickupLocation
    },
    type: HoldsConstants.UPDATE_PICKUP_LOCATION
  };
}

export function savePickupLocation({ holdId, accountId, branch }) {
  return {
    holdId,
    branch,
    middlewareData: {
      accountRequired: true
    },
    types: [
      HoldsConstants.UPDATE_PICKUP_LOCATION_REQUEST,
      HoldsConstants.UPDATE_PICKUP_LOCATION_SUCCESS,
      HoldsConstants.UPDATE_PICKUP_LOCATION_FAILURE
    ],
    apiRequest: (state, client) =>
      HoldApi.updateHolds(
        {
          holdIds: [holdId],
          accountId,
          location: branch.get('code')
        },
        client
      ),
    apiSuccess: () => ({
      messaging: composeGlobalSuccessMessaging(
        'message_successful_update_pickup_location',
        { values: { location: branch.get('name') } }
      )
    })
  };
}

export function fetchHolds({ accountId, size = 25, status, page = 1, sort = 'status', materialType }) {
  return {
    types: [HoldsConstants.FETCH_HOLDS_REQUEST, HoldsConstants.FETCH_HOLDS_SUCCESS, HoldsConstants.FETCH_HOLDS_FAILURE],
    middlewareData: {
      accountRequired: true
    },
    apiRequest: (state, apiClient) => HoldApi.getHolds({
      accountId,
      size,
      status: status && status.toUpperCase(),
      page,
      sort,
      materialType
    }, apiClient)
  };
}

export function fetchExpiredHolds({ accountId, page = 1, size = 25 }) {
  return {
    types: [HoldsConstants.FETCH_EXPIRED_HOLDS_REQUEST, HoldsConstants.FETCH_EXPIRED_HOLDS_SUCCESS, HoldsConstants.FETCH_EXPIRED_HOLDS_FAILURE],
    middlewareData: {
      accountRequired: true
    },
    apiRequest: (state, apiClient) => HoldApi.getHolds({ accountId, status: 'EXPIRED', page, size }, apiClient)
  };
}

export function fetchCancelledHolds({ accountId, page = 1, size = 25 }) {
  return {
    types: [HoldsConstants.FETCH_CANCELLED_HOLDS_REQUEST, HoldsConstants.FETCH_CANCELLED_HOLDS_SUCCESS, HoldsConstants.FETCH_CANCELLED_HOLDS_FAILURE],
    middlewareData: {
      accountRequired: true
    },
    apiRequest: (state, apiClient) => HoldApi.getHolds({ accountId, status: 'CANCELLED', page, size }, apiClient)
  };
}

const getFirstFailure = (response) => {
  const failures = response.failures || [];
  if (failures.length > 0) {
    return failures[0].errorResponseDTO || {};
  }

  return null;
};

const getMessageForUpdateHolds = (response, successMessage) => {
  const failure = getFirstFailure(response);

  return failure ?
    composeGlobalFailureMessaging(failure) :
    successMessage;
};

export function updateHoldsPauseEndDate({ accountId, holdIds, workflowId, endDate, formattedEndDate }) {
  return {
    holdIds,
    workflowId,
    middlewareData: {
      accountRequired: true
    },
    types: [
      HoldsConstants.UPDATE_PAUSE_HOLDS_DATE_REQUEST,
      HoldsConstants.UPDATE_PAUSE_HOLDS_DATE_SUCCESS,
      HoldsConstants.UPDATE_PAUSE_HOLDS_DATE_FAILURE],
    apiRequest: (state, client) =>
      HoldApi.updateHolds(
        {
          holdIds,
          accountId,
          suspended: {
            status: true,
            endDate
          }
        },
        client
      ),
    apiSuccess: ({ failures }) => {
      const messaging = composeMessagingForBatchAction({
        total: holdIds.length,
        failures,
        successId: 'message_successful_update_pause_end_date',
        mixedId: 'message_mixed_update_date',
        failureId: 'message_failure_update_date',
        values: { date: formattedEndDate }
      });

      return {
        messaging
      };
    },
    apiFailure: () => ({
      messaging: composeMessaging(
        STATUS_TYPES.Failure,
        GLOBAL_MESSAGING_ID,
        'message_failure_update_date',
        { values: { count: holdIds.length } }
      )
    })
  };
}

export function pauseOrResumeHolds({ accountId, holdIds, workflowId, suspended }) {
  return {
    holdIds,
    workflowId,
    suspended,
    middlewareData: {
      accountRequired: true
    },
    types: [
      HoldsConstants.UPDATE_PAUSE_HOLDS_REQUEST,
      HoldsConstants.UPDATE_PAUSE_HOLDS_SUCCESS,
      HoldsConstants.UPDATE_PAUSE_HOLDS_FAILURE
    ],
    apiRequest: (state, client) =>
      HoldApi.updateHolds(
        {
          holdIds,
          accountId,
          suspended
        },
        client
      ),
    apiSuccess: ({ failures }) => {
      const action = suspended.status === true ? 'pause' : 'resume';
      const messaging = composeMessagingForBatchAction({
        total: holdIds.length,
        failures,
        successId: `message_successful_${action}_holds`,
        mixedId: `message_mixed_${action}_holds`,
        failureId: `message_failure_${action}_holds`
      });

      return {
        messaging
      };
    }
  };
}

export function updateHoldsExpiryDate({ accountId, holdIds, workflowId, expiryDate, formattedExpiryDate }) {
  return {
    holdIds,
    workflowId,
    middlewareData: {
      accountRequired: true
    },
    types: [
      HoldsConstants.UPDATE_HOLDS_EXPIRY_REQUEST,
      HoldsConstants.UPDATE_HOLDS_EXPIRY_SUCCESS,
      HoldsConstants.UPDATE_HOLDS_EXPIRY_FAILURE
    ],
    apiRequest: (state, client) =>
      HoldApi.updateHolds(
        {
          holdIds,
          accountId,
          expiryDate
        },
        client
      ),
    apiSuccess: (response) => {
      const successMessage = composeGlobalSuccessMessaging('message_successful_update_expiry_date', { values: { date: formattedExpiryDate } });

      return {
        messaging: getMessageForUpdateHolds(response, successMessage)
      };
    }
  };
}

export default {
  placeHold,
  placeItemHold,
  cancelHold,
  startPlaceHold,
  startCancelHold,
  getItemGroups,
  updatePickupLocation,
  fetchHolds,
  fetchExpiredHolds,
  fetchCancelledHolds,
  updateHoldsPauseEndDate,
  pauseOrResumeHolds,
  updateHoldsExpiryDate
};



// WEBPACK FOOTER //
// ./app/actions/HoldActions.js